import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  Center,
  CloseIcon,
  DownloadIcon,
  HStack,
  Icon,
  Image,
  Pressable,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  useToast,
  VStack,
} from '@gluestack-ui/themed';
import { useMutation, useQuery } from '@tanstack/react-query';
import { EditIcon } from 'lucide-react-native';
import type { FC } from 'react';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { AnalysisStepper } from '../components/analysis/AnalysisStepper';
import { DetectionResults } from '../components/analysis/detection/DetectionResults';
import { HypothesisComparison } from '../components/analysis/verrou/HypothesisComparison';
import { StrainPrediction } from '../components/analysis/strain/StrainPrediction';
import { BiochemicalVerification } from '../components/analysis/verification/BiochemicalVerification';
import { PrimaryButton } from '../components/buttons/PrimaryButton';
import { Title } from '../components/Titles/Title';
import { TranslationContext } from '../contexts/translations/Translation';
import { UserContext } from '../contexts/User';
import { AnalysisDTO, AnalysisService, StrainDTO, StrainService } from '../generated/client-api-client';
import type { IAnalysisPageProps } from '../routes/Routes';
import { Routes } from '../routes/Routes';
import { COLORS } from '../theme/colors';
import { styles } from '../theme/styles';
import { BiochemicalTestMap } from '../utils/MapUtils';

type DetailsStep = 'detection' | 'strain' | 'verification';

const stepperIndex: Record<DetailsStep, 0 | 1 | 2 | 3> = {
  detection: 1,
  strain: 2,
  verification: 3,
};

export const AnalysisDetails: FC<IAnalysisPageProps> = (props: IAnalysisPageProps) => {
  const { navigation } = props;
  const { analysisId, testID } = props.route.params;
  const [detailsStep, setDetailsStep] = useState<DetailsStep>('detection');
  const [refetchEvery5Secs, setRefetchEvery5Secs] = useState<boolean>(false);
  const pendingBiochemicalResultsRef = useRef<Record<string, string | null> | null>(null);
  const { i18n } = useContext(TranslationContext);
  const user = useContext(UserContext);

  const { close, show } = useToast();

  const showStartAnalysisJobErrorToast = () => {
    show({
      id: 'startAnalysisJobError',
      placement: 'bottom left',
      duration: 10_000,
      render: ({ id }) => (
        <Toast action="error" variant="accent" alignItems="center" gap={10}>
          <ToastTitle>{i18n?.t('page.analysis-details.toast.start-analysis-job-error')}</ToastTitle>
          <Pressable onPress={() => close(id)}>
            <Icon as={CloseIcon} />
          </Pressable>
        </Toast>
      ),
    });
  };

  const {
    data: analysis,
    isLoading: isAnalysisLoading,
    refetch: refetchAnalysis,
  } = useQuery<AnalysisDTO>({
    queryKey: ['analysis', analysisId, user],
    queryFn: () => AnalysisService.analysisControllerReadOne(user!.currentWorkspace!.id!, analysisId),
    refetchInterval: refetchEvery5Secs ? 5_000 : 0,
  });

  useEffect(() => {
    const isProcessing = analysis?.status === AnalysisDTO.status.PROCESSING;
    setRefetchEvery5Secs(isProcessing);
  }, [analysis?.status]);

  useEffect(() => {
    if (analysis?.strainResults && detailsStep === 'strain') {
      setRefetchEvery5Secs(false);
    }
  }, [analysis?.strainResults, detailsStep]);

  // When returning to a confirmed analysis, jump straight to the strain (step 3) view
  useEffect(() => {
    if (analysis?.status === AnalysisDTO.status.CONFIRMED && detailsStep === 'detection') {
      setDetailsStep('strain');
    }
  }, [analysis?.status]);

  // UAT fallback: when default model routes results directly to strainResults (no detection phase)
  useEffect(() => {
    if (
      analysis?.status === AnalysisDTO.status.COMPLETED &&
      analysis?.strainResults &&
      !analysis?.results &&
      detailsStep === 'detection'
    ) {
      setDetailsStep('strain');
    }
  }, [analysis?.status, analysis?.strainResults, analysis?.results]);

  const topStrainName = (() => {
    if (!analysis?.strainResults) return '';
    const entries = Object.entries(analysis.strainResults as Record<string, number>);
    return entries.sort(([, a], [, b]) => b - a)[0]?.[0] ?? '';
  })();

  const { data: selectedStrain } = useQuery<StrainDTO | null>({
    queryKey: ['strain', topStrainName],
    queryFn: async () => {
      if (!topStrainName) return null;
      const results = await StrainService.strainControllerSearch(topStrainName, undefined, 0, 10);
      return results.data.find((s) => s.commonName?.toLowerCase().trim() === topStrainName.toLowerCase().trim()) ?? null;
    },
    enabled: !!topStrainName,
  });

  const neededTests = BiochemicalTestMap.filter(
    (key) => (selectedStrain as any)?.biochemicalTestNeeded?.[key],
  );

  const selectedOrganisms = (() => {
    const raw = (analysis?.properties as any)?.organisms;
    if (!raw) return ['bacteria', 'yeast', 'mold'];
    return typeof raw === 'string' ? raw.split(',') : raw;
  })();

  const { mutateAsync: startAnalysis, isPending: isStartAnalysisPending } = useMutation({
    mutationFn: async () => {
      try {
        await AnalysisService.analysisControllerStartAnalysis(user!.currentWorkspace!.id!, analysisId);
      } catch {
        showStartAnalysisJobErrorToast();
      }
    },
  });

  const { mutateAsync: startStrainAnalysis, isPending: isStrainAnalysisPending } = useMutation({
    mutationFn: async () => {
      await AnalysisService.analysisControllerStartStrainAnalysis(user!.currentWorkspace!.id!, analysisId);
    },
    onSuccess: () => {
      setDetailsStep('strain');
      setRefetchEvery5Secs(true);
    },
    onError: () => showStartAnalysisJobErrorToast(),
  });

  const { mutateAsync: confirmPathogen, isPending: isConfirmPathogenLoading } = useMutation({
    mutationFn: async () => {
      return AnalysisService.analysisControllerConfirmResultAnalysis(
        user!.currentWorkspace!.id!,
        analysisId,
        {
          pathogenName: topStrainName,
          biochemicalResults: pendingBiochemicalResultsRef.current as Record<string, string> ?? undefined,
        },
      );
    },
    onSuccess: async () => {
      const { data: freshAnalysis } = await refetchAnalysis();
      navigation.navigate(Routes.ANALYSIS_EXPORT, {
        analysis: (freshAnalysis ?? analysis)!,
        reportType: 'strain',
        biochemicalResults: pendingBiochemicalResultsRef.current ?? undefined,
        neededTests: neededTests.length > 0 ? neededTests : undefined,
        strain: selectedStrain ?? undefined,
      });
    },
    onError: () => showStartAnalysisJobErrorToast(),
  });

  if (isAnalysisLoading) {
    return (
      <Box>
        <Spinner size={20} />
      </Box>
    );
  }

  return (
    <>
      {analysis && (
        <Box style={styles.layout}>
          <Box display="flex" flexDirection="row" justifyContent="space-between" flexWrap="wrap">
            <Center>
              <HStack>
                <Title title={i18n?.t('page.analysis-details.title')} />
                <Center margin={10}>
                  <Pressable
                    onPress={() => navigation.navigate(Routes.ANALYSIS_FORM, { analysisList: [analysis] })}
                    alignSelf="center"
                  >
                    <ButtonIcon as={EditIcon} size="md" />
                  </Pressable>
                </Center>
              </HStack>
            </Center>
            <Center>
              <Box style={{ backgroundColor: 'white', borderRadius: 10, padding: 10, shadowColor: '#171717', shadowOffset: { width: -2, height: 4 }, shadowOpacity: 0.2, shadowRadius: 3 }}>
                <Text>
                  {new Date(analysis.createdAt).toLocaleDateString(i18n?.locale)} | {new Date(analysis.createdAt).toLocaleTimeString(i18n?.locale)}
                </Text>
              </Box>
            </Center>
          </Box>

          <Box flexWrap="wrap" flexDirection="row" display="flex" style={{ marginVertical: 10, gap: 30, marginTop: 50 }}>
            <Image
              crossOrigin="anonymous"
              source={{ uri: analysis.imageUrl }}
              style={{
                aspectRatio: '1/1',
                borderRadius: 10,
                maxHeight: 450,
                maxWidth: 450,
                minHeight: 150,
                minWidth: 150,
                height: '100%',
                width: '100%',
                flexGrow: 1,
              }}
            />
            <VStack
              style={{
                backgroundColor: 'white',
                borderRadius: 10,
                paddingVertical: 20,
                paddingHorizontal: 30,
                shadowColor: '#171717',
                shadowOffset: { width: -2, height: 4 },
                shadowOpacity: 0.2,
                shadowRadius: 3,
                flexGrow: 1,
              }}
            >
              {/* IDLE — start analysis */}
              {analysis.status === AnalysisDTO.status.IDLE && (
                <>
                  <AnalysisStepper currentStep={0} />
                  {(user?.scansInformation?.available ?? 0) === 0 ? (
                    <Text fontWeight="bold">{i18n?.t('page.analysis-details.info-card.ask-for-more-scans')}</Text>
                  ) : (
                    <PrimaryButton
                      onPress={async () => { await startAnalysis(); await refetchAnalysis(); }}
                      content={i18n?.t('page.analysis-details.start-scan.title')}
                      isLoading={isStartAnalysisPending}
                      minWidth={150}
                      width="auto"
                    />
                  )}
                </>
              )}

              {/* PROCESSING — spinner */}
              {analysis.status === AnalysisDTO.status.PROCESSING && (
                <>
                  <AnalysisStepper currentStep={detailsStep === 'strain' ? 2 : 1} />
                  <VStack style={{ flex: 1, margin: 10 }}>
                    <Text fontWeight="bold">{i18n?.t('page.analysis-details.info-card.no-results')}</Text>
                    <Spinner size="large" />
                  </VStack>
                </>
              )}

              {/* COMPLETED — detection / strain / verification steps */}
              {analysis.status === AnalysisDTO.status.COMPLETED && (
                <>
                  <AnalysisStepper currentStep={stepperIndex[detailsStep]} />

                  {detailsStep === 'detection' && (() => {
                    const detectionResults = (analysis.results ?? {}) as Record<string, number | string>;
                    const onInvestigate = async () => {
                      if (analysis.strainResults) {
                        setDetailsStep('strain');
                      } else {
                        await startStrainAnalysis();
                      }
                    };
                    const onDownloadReport = () => navigation.navigate(Routes.ANALYSIS_EXPORT, { analysis, reportType: 'detection' });
                    const onBack = () => navigation.goBack();
                    return (
                    <VStack gap={16}>
                      {/* Detection result (Bactérie) first, then the hypothesis comparison, then actions */}
                      <DetectionResults
                        results={detectionResults}
                        selectedOrganisms={selectedOrganisms}
                        onInvestigate={onInvestigate}
                        onDownloadReport={onDownloadReport}
                        onBack={onBack}
                        hideActions
                        testID={testID}
                      />
                      <HypothesisComparison
                        hypothesis={(analysis.properties as any)?.userHypothesis}
                        results={detectionResults}
                        coloration={analysis.coloration}
                        testID={testID}
                      />
                      <DetectionResults
                        results={detectionResults}
                        selectedOrganisms={selectedOrganisms}
                        onInvestigate={onInvestigate}
                        onDownloadReport={onDownloadReport}
                        onBack={onBack}
                        isInvestigateLoading={isStrainAnalysisPending}
                        actionsOnly
                        testID={testID}
                      />
                    </VStack>
                    );
                  })()}

                  {detailsStep === 'strain' && (
                    <StrainPrediction
                      strainResults={analysis.strainResults as Record<string, number> | null}
                      topStrainName={topStrainName}
                      strain={selectedStrain ?? null}
                      onProceedToVerification={() => setDetailsStep('verification')}
                      onBack={() => setDetailsStep('detection')}
                      testID={testID}
                    />
                  )}

                  {detailsStep === 'verification' && selectedStrain && (
                    <BiochemicalVerification
                      strain={selectedStrain}
                      neededTests={neededTests}
                      topStrainConfidence={topStrainName && analysis.strainResults ? parseFloat(((analysis.strainResults as Record<string, number>)[topStrainName] * 100).toFixed(1)).toString() : undefined}
                      onConfirm={async (vals) => { pendingBiochemicalResultsRef.current = vals as Record<string, string | null>; await confirmPathogen(); }}
                      onReportDiscordance={(vals) => {
                        navigation.navigate(Routes.ANALYSIS_EXPORT, {
                          analysis: analysis!,
                          reportType: 'strain',
                          biochemicalResults: vals as Record<string, string | null>,
                          neededTests: neededTests.length > 0 ? neededTests : undefined,
                          strain: selectedStrain ?? undefined,
                        });
                      }}
                      onBack={() => setDetailsStep('strain')}
                      isLoading={isConfirmPathogenLoading}
                      testID={testID}
                    />
                  )}
                </>
              )}

              {/* CONFIRMED — read-only step navigation */}
              {analysis.status === AnalysisDTO.status.CONFIRMED && (
                <>
                  <AnalysisStepper currentStep={stepperIndex[detailsStep]} />

                  {detailsStep === 'detection' && (
                    <DetectionResults
                      results={(analysis.results ?? {}) as Record<string, number | string>}
                      selectedOrganisms={selectedOrganisms}
                      onInvestigate={() => setDetailsStep('strain')}
                      isInvestigateLoading={false}
                      onDownloadReport={() => navigation.navigate(Routes.ANALYSIS_EXPORT, { analysis, reportType: 'detection' })}
                      onBack={() => navigation.goBack()}
                      testID={testID}
                    />
                  )}

                  {detailsStep === 'strain' && (
                    <StrainPrediction
                      strainResults={analysis.strainResults as Record<string, number> | null}
                      topStrainName={topStrainName}
                      strain={selectedStrain ?? null}
                      onProceedToVerification={() => setDetailsStep('verification')}
                      onBack={() => setDetailsStep('detection')}
                      testID={testID}
                    />
                  )}

                  {detailsStep === 'verification' && (
                    <VStack gap={12}>
                      <Box style={{ backgroundColor: '#F0FBF5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#A8DFC0' }}>
                        <Text fontWeight="bold" color={COLORS.STATUS.SUCCESS}>
                          {i18n?.t('page.analysis-details.info-card.analysis-confirmed')}
                        </Text>
                        <Text fontSize={14} marginTop={4}>{analysis.confirmedResult}</Text>
                      </Box>
                      <Button
                        backgroundColor="transparent"
                        borderWidth={1}
                        borderColor={COLORS.NEUTRAL[70]}
                        borderRadius={10}
                        width="100%"
                        onPress={() => navigation.navigate(Routes.ANALYSIS_EXPORT, {
                          analysis,
                          reportType: 'strain',
                          biochemicalResults: (analysis.properties as any)?.biochemicalResults ?? undefined,
                          neededTests: neededTests.length > 0 ? neededTests : undefined,
                          strain: selectedStrain ?? undefined,
                        })}
                      >
                        <ButtonText paddingRight={4} style={{ color: COLORS.FOUNDATION_TERCIARY_100 }}>
                          {i18n?.t('page.analysis-details.export-to-pdf-button.title')}
                        </ButtonText>
                        <ButtonIcon as={DownloadIcon} color={COLORS.FOUNDATION_TERCIARY_100} />
                      </Button>
                      <Pressable onPress={() => setDetailsStep('strain')} alignSelf="center" marginTop={4}>
                        <Text fontSize={13} color={COLORS.ACCENT[100]}>{i18n?.t('common.back')}</Text>
                      </Pressable>
                    </VStack>
                  )}
                </>
              )}

              {/* ERROR */}
              {analysis.status === AnalysisDTO.status.ERROR && (
                <Text color={COLORS.ACCENT[100]}>{i18n?.t('page.analysis-details.toast.start-analysis-job-error')}</Text>
              )}
            </VStack>
          </Box>
        </Box>
      )}
    </>
  );
};
